import React, { useEffect } from "react";
import { Form, Input, Cascader, Select, Switch, Typography } from "antd";
import Map2 from "../Map/Map2";

const { Option } = Select;
const { Text } = Typography;

const AssetForm = props => {
  const [form] = Form.useForm();
  const isEditMode = props.isEditMode || false;

  // Set form values when the asset changes
  useEffect(() => {
    if (props.record.asset) {
      const typeArray = props.record.asset.typePath ?
        props.record.asset.typePath.split('/') :
        [];

      form.setFieldsValue({
        name: props.record.asset.name,
        geometry: props.record.asset.geometry,
        insured: props.record.asset.insured,
        culturalHeritage: props.record.asset.culturalHeritage,
        type: typeArray
      });
    }
  }, [props.record.asset, form]);


  const formItemLayout = {
    labelCol: {
      sm: { span: 4 }
    },
    wrapperCol: {
      sm: { span: 20 }
    }
  };

  const handleFormChange = () => {
    if (!isEditMode) return;

    const values = form.getFieldsValue();

    // Format for parent component
    const newAsset = { ...props.record };
    newAsset.asset.name = values.name;

    if (values.type && values.type.length > 0) {
      // Set typePath - this is used by the backend
      newAsset.asset.typePath = values.type.join('/');

      // Set componentKind to the last value in the path
      const lastPos = values.type.length - 1;
      newAsset.asset.componentKind = values.type[lastPos];

      // Find the selected type in assetTypes to get its name
      let currentLevel = props.assetTypes;

      // Try to find the type at each level of the hierarchy
      for (let i = 0; i < values.type.length; i++) {
        const currentId = values.type[i];
        const foundType = currentLevel.find(type => type.value === currentId);

        if (foundType) {
          // If this is the last level, we've found our type
          if (i === lastPos) {
            newAsset.asset.typeName = foundType.name;
            newAsset.asset.typeDescriptionPath = foundType.label || foundType.name;
          }

          // Move to next level if there are children
          if (foundType.children) {
            currentLevel = foundType.children;
          } else {
            break;
          }
        } else {
          break;
        }
      }
    }

    newAsset.asset.geometry = values.geometry;
    newAsset.asset.insured = values.insured;
    newAsset.asset.culturalHeritage = values.culturalHeritage;

    // Only check validation in edit mode
    let isValid = true;
    // Check for existing errors without triggering new validation
    const errors = form.getFieldsError();
    const hasErrors = errors.some(error => error.errors.length > 0);

    // Check for required empty fields
    const requiredFields = ['name', 'type'];
    const hasEmptyRequired = requiredFields.some(field => {
      // For name, check if it's empty string
      if (field === 'name') {
        return !values[field] || values[field].trim() === '';
      }
      // For type, check if it's empty array
      if (field === 'type') {
        return !values[field] || values[field].length === 0;
      }
      return !values[field];
    });

    isValid = !hasErrors && !hasEmptyRequired;

    newAsset.valid = isValid;
    props.parentCallback(newAsset);
  };

  // Determine if the current asset is read-only
  const isReadOnly = props.record.asset && (
    props.record.asset.readonly === true || 
    // Also check typeName for component types that are known to be read-only
    (props.record.asset.typeName && 
     ['electrical_service', 'transport_service', 'tlc_service']
     .includes(props.record.asset.typeName))
  );


  return (
    <Form
      form={form}
      {...formItemLayout}
      className="details_form"
      onValuesChange={handleFormChange}
      onFieldsChange={handleFormChange}
    >
      <Form.Item
        label="Name"
        name="name"
        rules={isEditMode ? [
          { required: true, message: "Please input Name!" },
          {
            validator: (_, value) => {
              if (value && value.trim() === '') {
                return Promise.reject('Name cannot be just whitespace');
              }
              return Promise.resolve();
            }
          }
        ] : []}
      >
        <Input placeholder="Name" disabled={!isEditMode} />
      </Form.Item>

      {/* For read-only assets, don't use a form field at all; just display text */}
      {isReadOnly ? (
        <Form.Item label="Type">
          <Text>
            {props.record.asset.typeDescriptionPath || props.record.asset.typeName || "Read-only component"}
          </Text>
        </Form.Item>
      ) : (
        <Form.Item
          label="Type"
          name="type"
          rules={isEditMode ? [
            { type: 'array', required: true, message: 'Please select Type!' },
          ] : []}
        >
          <Cascader
            options={props.assetTypes}
            disabled={!isEditMode}
          />
        </Form.Item>
      )}


      <Form.Item
        label="Insured"
        name="insured"
        valuePropName="checked"
      >
        <Switch disabled={!isEditMode} />
      </Form.Item>

      <Form.Item
        label="Cultural heritage"
        name="culturalHeritage"
        valuePropName="checked"
      >
        <Switch disabled={!isEditMode} />
      </Form.Item>

      <Form.Item
        label="Position"
        name="geometry"
      >
        <Map2 name="position" disabled={!isEditMode}></Map2>
      </Form.Item>
    </Form>
  );
};

export default AssetForm;